import { ControlledInput } from "components/Card/ControlledInput";
import { ControlledTextArea, MaskedInput } from "components/inputs";
import ControlledSelect from "components/inputs/Select/ControlledSelect";
import { Fragment, useEffect } from "react";
import type { Control, Path, UseFormSetValue } from "react-hook-form";
import { useUpdateLienArgumentMutation } from "services/lien-api";
import type { ConstantTypes } from "types/DataTypes";

import type {
  ArgumentTypeChoices,
  DisputeType,
  LienArgument,
  LienFormValues,
} from "../../LienTypes";

type ArgumentsSectionProps = {
  control: Control<LienFormValues>;
  lienArguments?: LienArgument[];
  constants?: ConstantTypes & {
    ArgumentTypeChoices?: ArgumentTypeChoices[];
  };
  accessToken: string;
  total_conditional_payment_by_argument_type: Record<DisputeType, number>;
  setValue: UseFormSetValue<LienFormValues>;
};

const ArgumentsSection = ({
  control,
  lienArguments,
  constants,
  total_conditional_payment_by_argument_type,
  setValue,
}: ArgumentsSectionProps) => {
  const [updateLienArgument] = useUpdateLienArgumentMutation();

  // group arguments by type to avoid duplicates
  const argumentsByType = lienArguments?.reduce(
    (acc, arg) => {
      // only keep the first argument of each type
      if (!acc[arg.argument_type]) {
        acc[arg.argument_type] = arg;
      }
      return acc;
    },
    {} as Record<DisputeType, LienArgument>,
  );

  // update form values with aggregated totals
  useEffect(() => {
    if (argumentsByType) {
      Object.entries(argumentsByType).forEach(([type, arg]) => {
        const fieldName = `arguments.${arg.id}.total` as Path<LienFormValues>;
        setValue(
          fieldName,
          total_conditional_payment_by_argument_type[type as DisputeType],
        );
      });
    }
  }, [argumentsByType, total_conditional_payment_by_argument_type, setValue]);

  const renderArgumentFields = (argument: LienArgument) => {
    const filteredOptions = constants?.ArgumentTypeChoices?.filter(
      (choice) => choice.value === argument.argument_type,
    );

    const argumentLabel =
      constants?.ArgumentTypeChoices?.find(
        (choice) => choice.value === argument.argument_type,
      )?.label || argument.argument_type;

    const fieldName = `arguments.${argument.id}.total` as Path<LienFormValues>;

    return (
      <Fragment key={argument.id}>
        <ControlledSelect<LienFormValues>
          control={control}
          name={
            `arguments.${argument.id}.argument_type` as Path<LienFormValues>
          }
          label="Dispute / Argument Type"
          errors={{}}
          options={filteredOptions || []}
          readOnly
          onSave={async () => Promise.resolve()}
        />

        <ControlledInput
          control={control}
          name={fieldName}
          label={`${argumentLabel} Amount`}
          errors={{}}
          readOnly
          componentType={MaskedInput}
          mask="dollar"
          onSave={async () => Promise.resolve()}
        />

        <ControlledTextArea
          control={control}
          name={`arguments.${argument.id}.rationale` as Path<LienFormValues>}
          label={`${argumentLabel} Rationale`}
          errors={{}}
          onSave={async (value) => {
            try {
              await updateLienArgument({
                id: argument.id,
                data: { rationale: String(value) },
              }).unwrap();
            } catch (error) {
              console.error("Error updating rationale:", error);
            }
          }}
          advancedEdit={true}
          maxLength={2000}
        />
      </Fragment>
    );
  };

  // only render unique arguments by type
  return <>{Object.values(argumentsByType || {}).map(renderArgumentFields)}</>;
};

export default ArgumentsSection;
